import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { ConfirmModal, RowActions, RowAction } from '@redpanda/ui';
import { enableShareLink, disableShareLink } from '../services/api';

/** 客戶驗收頁網址（前端公開路由 /acceptance/:token） */
const acceptanceUrl = (token: string) => `${window.location.origin}/acceptance/${token}`;

/**
 * 專案總覽的「客戶驗收連結」：建立、複製、重新產生、停用
 * 重新產生與停用都會讓已經給客戶的舊連結失效，所以要先確認
 */
const AcceptanceLinkControl: React.FC<{ projectId: string; shareToken?: string | null; onChange: (token: string | null) => void }> = ({
  projectId,
  shareToken,
  onChange,
}) => {
  const { t } = useTranslation(['translation', 'pages']);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState<'regenerate' | 'disable' | null>(null);

  const run = async (action: () => Promise<string | null>, success: string) => {
    setBusy(true);
    try {
      onChange(await action());
      toast.success(success);
    } catch (error: any) {
      toast.error(error.response?.data?.error || t('common.messages.operationFailed'));
    } finally {
      setBusy(false);
    }
  };
  const create = () =>
    run(async () => (await enableShareLink(projectId)).data.shareToken, t('pmProjects.acceptanceLink.created', { ns: 'pages' }));
  const disable = () =>
    run(async () => { await disableShareLink(projectId); return null; }, t('pmProjects.acceptanceLink.disabled', { ns: 'pages' }));
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(acceptanceUrl(shareToken!));
      toast.success(t('pmProjects.acceptanceLink.copied', { ns: 'pages' }));
    } catch {
      toast.error(t('common.messages.operationFailed'));
    }
  };

  return (
    <>
      {shareToken ? (
        <div className="min-w-0">
          <a href={acceptanceUrl(shareToken)} target="_blank" rel="noopener noreferrer" className="block truncate text-[13px] text-accent hover:underline">
            {acceptanceUrl(shareToken)}
          </a>
          <RowActions>
            <RowAction onClick={copy}>{t('common.actions.copy')}</RowAction>
            <RowAction onClick={() => setConfirm('regenerate')} disabled={busy}>{t('pmProjects.acceptanceLink.regenerate', { ns: 'pages' })}</RowAction>
            <RowAction tone="danger" onClick={() => setConfirm('disable')} disabled={busy}>{t('pmProjects.acceptanceLink.disable', { ns: 'pages' })}</RowAction>
          </RowActions>
        </div>
      ) : (
        <div>
          <RowActions>
            <RowAction onClick={create} disabled={busy}>{t('pmProjects.acceptanceLink.create', { ns: 'pages' })}</RowAction>
          </RowActions>
          <p className="mt-0.5 text-[13px] text-fg-secondary">{t('pmProjects.acceptanceLink.hint', { ns: 'pages' })}</p>
        </div>
      )}

      <ConfirmModal
        isOpen={confirm !== null}
        onClose={() => setConfirm(null)}
        onConfirm={() => {
          const action = confirm;
          setConfirm(null);
          if (action === 'regenerate') create();
          else disable();
        }}
        title={t(`pmProjects.acceptanceLink.${confirm === 'regenerate' ? 'regenerate' : 'disable'}`, { ns: 'pages' })}
        message={t('pmProjects.acceptanceLink.invalidateWarning', { ns: 'pages' })}
        confirmText={t('common.actions.confirm')}
        cancelText={t('common.actions.cancel')}
        t={t}
      />
    </>
  );
};

export default AcceptanceLinkControl;
